import React, { useContext, useEffect, useState } from 'react';
import { Box, Button, TextField, Typography } from '@material-ui/core';
import { AlertContext } from '../../Context/AlertContextProvider';
import {
  getFeatureRequestComments,
  addFeatureRequestComment,
  updateFeatureRequestComment,
  deleteFeatureRequestComment,
} from './featureRequestActions';

// Threaded, anonymized comments. Authors arrive from the hub already pseudonymized
// ("Customer 1", "Requester", "ShowroomPricing") with an is_mine flag for "You".
// Users can edit/delete their own comments; when ShowroomPricing closes the thread
// (comments_locked) the composer and edit controls disappear (delete-own still works).
export default function FeatureRequestComments({ featureRequestId, onCountChange }) {
  const { handleAlertOpen, setMessage, setMessageType } = useContext(AlertContext);

  const [comments, setComments] = useState([]);
  const [locked, setLocked] = useState(false);
  const [newComment, setNewComment] = useState('');
  const [replyTo, setReplyTo] = useState(null);
  const [replyText, setReplyText] = useState('');
  const [editing, setEditing] = useState(null);
  const [editText, setEditText] = useState('');
  const [saving, setSaving] = useState(false);

  const load = () => {
    getFeatureRequestComments(featureRequestId)
      .then((res) => {
        const list = res.data.comments || [];
        setComments(list);
        setLocked(Boolean(res.data.comments_locked));
        onCountChange &&
          onCountChange(list.filter((c) => !c.is_deleted).length);
      })
      .catch(() => setComments([]));
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [featureRequestId]);

  const fail = (err, fallback) => {
    setMessage(err?.response?.data?.message || fallback);
    setMessageType('error');
    handleAlertOpen();
  };

  const submit = (text, parentId) => {
    if (!text.trim()) return;
    setSaving(true);
    addFeatureRequestComment(featureRequestId, text.trim(), parentId)
      .then(() => {
        setNewComment('');
        setReplyTo(null);
        setReplyText('');
        load();
      })
      .catch((err) => fail(err, 'Error adding comment'))
      .finally(() => setSaving(false));
  };

  const saveEdit = (commentId) => {
    if (!editText.trim()) return;
    setSaving(true);
    updateFeatureRequestComment(featureRequestId, commentId, editText.trim())
      .then(() => {
        setEditing(null);
        setEditText('');
        load();
      })
      .catch((err) => fail(err, 'Error updating comment'))
      .finally(() => setSaving(false));
  };

  const handleDelete = (commentId) => {
    // eslint-disable-next-line no-alert
    if (!window.confirm('Delete your comment?')) return;
    deleteFeatureRequestComment(featureRequestId, commentId)
      .then(() => load())
      .catch((err) => fail(err, 'Error deleting comment'));
  };

  const authorStyle = (c) => {
    if (c.is_staff) return { color: '#FF682C', fontWeight: 700 };
    if (c.is_mine) return { color: '#1565c0', fontWeight: 700 };
    return { fontWeight: 700 };
  };

  const renderThread = (parentId, depth) => {
    const children = comments.filter(
      (c) => (c.parent_comment_id || null) === parentId
    );
    return children.map((c) => (
      <Box
        key={c.feature_request_comment_id}
        style={{
          marginLeft: depth * 24,
          marginTop: 8,
          paddingLeft: depth ? 12 : 0,
          borderLeft: depth ? '2px solid #eee' : 'none',
        }}
      >
        <Typography variant="body2">
          {c.is_deleted ? (
            <span style={{ color: '#999', fontStyle: 'italic' }}>[deleted]</span>
          ) : (
            <span style={authorStyle(c)}>{c.is_mine ? 'You' : c.author}</span>
          )}
          <span style={{ color: '#999', marginLeft: 8, fontSize: 12 }}>
            {c.created_dttm ? new Date(c.created_dttm).toLocaleString() : ''}
            {c.is_edited ? ' (edited)' : ''}
          </span>
        </Typography>

        {editing === c.feature_request_comment_id ? (
          <Box style={{ display: 'flex', gap: 8, marginTop: 4 }}>
            <TextField
              size="small"
              variant="outlined"
              fullWidth
              multiline
              value={editText}
              onChange={(e) => setEditText(e.target.value)}
            />
            <Button
              size="small"
              variant="contained"
              style={{ background: '#FF682C', color: '#fff' }}
              disabled={saving || !editText.trim()}
              onClick={() => saveEdit(c.feature_request_comment_id)}
            >
              Save
            </Button>
            <Button size="small" onClick={() => setEditing(null)}>
              Cancel
            </Button>
          </Box>
        ) : (
          <Typography
            variant="body2"
            style={{
              whiteSpace: 'pre-wrap',
              ...(c.is_deleted ? { color: '#999', fontStyle: 'italic' } : {}),
            }}
          >
            {c.comment}
          </Typography>
        )}

        {!c.is_deleted && editing !== c.feature_request_comment_id && (
          <>
            {!locked && (
              <Button
                size="small"
                style={{ color: '#FF682C', padding: '0 4px', minWidth: 0 }}
                onClick={() => {
                  setReplyTo(
                    replyTo === c.feature_request_comment_id
                      ? null
                      : c.feature_request_comment_id
                  );
                  setReplyText('');
                }}
              >
                Reply
              </Button>
            )}
            {c.is_mine && !locked && (
              <Button
                size="small"
                style={{ color: '#888', padding: '0 4px', minWidth: 0 }}
                onClick={() => {
                  setEditing(c.feature_request_comment_id);
                  setEditText(c.comment);
                  setReplyTo(null);
                }}
              >
                Edit
              </Button>
            )}
            {c.is_mine && (
              <Button
                size="small"
                style={{ color: '#c62828', padding: '0 4px', minWidth: 0 }}
                onClick={() => handleDelete(c.feature_request_comment_id)}
              >
                Delete
              </Button>
            )}
          </>
        )}

        {replyTo === c.feature_request_comment_id && !locked && (
          <Box style={{ display: 'flex', gap: 8, marginTop: 4 }}>
            <TextField
              size="small"
              variant="outlined"
              fullWidth
              multiline
              placeholder="Write a reply…"
              value={replyText}
              onChange={(e) => setReplyText(e.target.value)}
            />
            <Button
              size="small"
              variant="contained"
              style={{ background: '#FF682C', color: '#fff' }}
              disabled={saving || !replyText.trim()}
              onClick={() => submit(replyText, c.feature_request_comment_id)}
            >
              Post
            </Button>
          </Box>
        )}
        {renderThread(c.feature_request_comment_id, depth + 1)}
      </Box>
    ));
  };

  return (
    <Box style={{ marginTop: 12, borderTop: '1px solid #eee', paddingTop: 8 }}>
      {comments.length === 0 && !locked && (
        <Typography variant="body2" color="textSecondary">
          No comments yet — start the discussion.
        </Typography>
      )}
      {renderThread(null, 0)}
      {locked ? (
        <Typography
          variant="body2"
          color="textSecondary"
          style={{ marginTop: 12, fontStyle: 'italic' }}
        >
          Comments are closed for this request.
        </Typography>
      ) : (
        <Box style={{ display: 'flex', gap: 8, marginTop: 12 }}>
          <TextField
            size="small"
            variant="outlined"
            fullWidth
            multiline
            placeholder="Add a comment…"
            value={newComment}
            onChange={(e) => setNewComment(e.target.value)}
          />
          <Button
            size="small"
            variant="contained"
            style={{ background: '#FF682C', color: '#fff' }}
            disabled={saving || !newComment.trim()}
            onClick={() => submit(newComment, null)}
          >
            Post
          </Button>
        </Box>
      )}
    </Box>
  );
}
